import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { confirmAlert } from "react-confirm-alert";
import { useLocation, useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { Button, Flex, Text, rem } from "@mantine/core";

import createPaymentIntent from "utils/createPaymentIntent";
import { STRIPE_AC_ID, BOOKING_ID } from "constants";
import { step as eventStep } from "store/atoms";
import { ROUTE_NAMES } from "routes/names";

import EventBuyingSummary from "views/TicketBooking/EventBuyingSummary";
import EventInfoCard from "views/TicketBooking/EventInfoCard";
import { Loader, ErrorAlert, UserDetails } from "components";
import { ArrowLeftIcon, CardIcon } from "assets/icon";
import { createBookingBody } from "core/methods";
import { TwAppBar, TwButton, TwFooter } from "components/tailwind";
import TandCLink from "components/TandCLink";
import { BOOKINK_CART } from "constants";
import { updatestate } from "store/reducer";
import { BOOKINK_LINK } from "constants";
import { useTranslation } from "react-i18next";

function CardPayment({ clientSecret }) {
  const { t } = useTranslation();
  const reduxstate = useSelector((state) => state.State);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [, setEventSelectedStep] = useAtom(eventStep);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    if (clientSecret) {
      stripe.retrievePaymentIntent(clientSecret).then((res) => {
        switch (res.paymentIntent.status) {
          case "succeeded":
            // setMessage("Payment succeeded!");
            break;
          case "processing":
            // setMessage("Your payment is processing.");
            break;
          case "requires_payment_method":
            // setMessage("Your payment was not successful, please try again.");
            break;
          default:
            // setMessage("Something went wrong.");
            break;
        }
      });
    }
  }, [stripe, elements, clientSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsSubmitting(true);

    let return_url = process.env.REACT_APP_STRIPE_RETURN_URL_DEV;

    switch (true) {
      case process.env.REACT_APP_ENVIRONMENT === "PRE_STAGING":
        return_url = process.env.REACT_APP_STRIPE_RETURN_URL_PRE_STAGING;
        break;
      case process.env.REACT_APP_ENVIRONMENT === "STAGING":
        return_url = process.env.REACT_APP_STRIPE_RETURN_URL_STAGING;
        break;
      case process.env.REACT_APP_ENVIRONMENT === "PROD":
        return_url = process.env.REACT_APP_STRIPE_RETURN_URL_PROD;
        break;
      default:
        break;
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: elements.getElement(CardElement),
        },
        return_url: `${return_url}${ROUTE_NAMES.PAYMENT_CHECK}`,
      }
    );

    if (paymentIntent) {
      if (paymentIntent.status !== "succeeded") {
        confirmAlert({
          customUI: ({ onClose }) => {
            return (
              <ErrorAlert
                error={t("error.paymentwasNotSuccessful")}
                serverError={false}
                onClose={() => {
                  onClose();
                  const eventName = reduxstate.eventData.eventName
                    .replace(/\s+/g, "-")
                    .toLowerCase();
                  const refId = reduxstate.eventData.referenceId;
                  setEventSelectedStep(1);

                  // navigate(`/booking/${eventName}/${refId}`);
                  navigate(`/${eventName}/${refId}`);
                }}
              />
            );
          },
        });
        return;
      }

      if (paymentIntent.status === "succeeded") {
        const params = new URLSearchParams();
        params.append("payment_intent", paymentIntent.id);

        navigate(`${ROUTE_NAMES.PAYMENT_CHECK}?${params.toString()}`, {
          state: { paymentSuccess: true },
        });
      }
    }

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.

    if (error.type === "card_error" || error.type === "validation_error") {
      setIsSubmitting(false);
      confirmAlert({
        customUI: ({ onClose }) =>
          ErrorAlert({ onClose, error: error.message, serverError: false }),
        closeOnEscape: false,
        closeOnClickOutside: false,
      });
    } else {
      setIsSubmitting(false);
      confirmAlert({
        customUI: ({ onClose }) =>
          ErrorAlert({
            onClose: () => {
              onClose();
              navigate(-1);
            },
            error: "An unexpected error occurred.",
            serverError: false,
          }),
        closeOnEscape: false,
        closeOnClickOutside: false,
      });
    }
  };

  return (
    <div className="[stripe-payment]">
      <div className="[stripe-container]">
        <form id="payment-form" onSubmit={handleSubmit} className="[bg-white] flex flex-col">
          <div className="[stripe-form] p-4 bg-white">
            <div className="[form-container] border-2 border-gray-300 rounded-xl p-4">
              <CardElement
                options={{
                  classes: {
                    base: "card-element-base",
                    focus: "card-element-focus",
                  },
                }}
              />
            </div>
          </div>
          <TwFooter isShowReservation={true}>
            <div className="p-4 bg-white flex flex-col gap-4">
              <TwButton
                className="w-[96%]"
                disabled={isSubmitting || !stripe || !elements}
                id="submit"
                actionLoading={isSubmitting}
                type="submit"
              >
                {t("button.payAndConfirm")}
              </TwButton>
              <TandCLink />
            </div>
          </TwFooter>
        </form>
      </div>
    </div>
  );
}

export default function ElementsWrapper() {

  const { t } = useTranslation();
  const BOOKINKLINK = window.localStorage.getItem(BOOKINK_LINK);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState();
  const [, setEventStep] = useAtom(eventStep);

  const reduxstate = useSelector((state) => state.State);
  const stripeAcId = window.localStorage.getItem(STRIPE_AC_ID);

  if (!BOOKINKLINK || BOOKINKLINK == null || BOOKINKLINK === "null") {
    navigate(`/`, { replace: true });
  } else if ((!stripeAcId || stripeAcId == null) && BOOKINKLINK && BOOKINKLINK !== "") {
    navigate(`${BOOKINKLINK}`, { replace: true });
  }

  useEffect(() => {
    let bookink_cart = window.localStorage.getItem(`${BOOKINK_CART}`);
    if (bookink_cart) {
      dispatch(updatestate({ ...Object.keys(bookink_cart).length > 0 ? JSON.parse(bookink_cart) : {} }));
    }
  }, []);

  useEffect(() => {
    const res = loadStripe(
      process.env.REACT_APP_ENVIRONMENT === "PROD"
        ? process.env.REACT_APP_STRIPE_LIVE_KEY
        : process.env.REACT_APP_STRIPE_TEST_KEY,
      stripeAcId ? { stripeAccount: stripeAcId } : undefined
    );

    setStripePromise(res);
  }, [stripeAcId]);

  let processingcheck = false
  useEffect(() => {
    if (processingcheck == false && reduxstate.addParticipantData) {
      (async () => {
        processingcheck = true
        const eventBookingPayload = createBookingBody({
          ...reduxstate.addParticipantData,
          paymentStatus: "Paid",
          paymentMethod: "creditCard",
          paymentMode: "Online",
        });

        const body = {
          eventScheduleId: reduxstate.id,
          selectedEventPricing: reduxstate.selectedEventPricing
            .filter((item) => item.bookingQty && item.bookingQty > 0)
            .map(({ bookingQty, totalPrice, ticketType }) => ({
              bookingQty,
              totalPrice,
              ticketType,
            })),
          selectedAddonsPricing: eventBookingPayload.selectedAddonsPricing,
          eventBookingPayload,
        };

        const { client_secret, orderReferenceId, error, message } = await createPaymentIntent({
          amount: Number(reduxstate.totalPrice),
          body,
          paymentMethod: "creditCard",
        });
        if (error == true) {
          confirmAlert({
            customUI: ({ onClose }) =>
              ErrorAlert({
                onClose: () => {
                  processingcheck = false
                  onClose();
                  navigate(-1, { replace: true });
                },
                error: message,
                serverError: false,
              }),
            closeOnEscape: false,
            closeOnClickOutside: false,
          });
          return;
        }
        window.localStorage.setItem(BOOKING_ID, orderReferenceId);
        setClientSecret(client_secret);
      })();
    }
    // }, [reduxstate.addParticipantData]);
  }, []);

  const handleChangePaymentMethod = () => {
    setEventStep(2);
    navigate(-1, { replace: true });
  };

  if (!stripePromise) return null;

  return (
    <>
      {clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          {/* <div className="[ticket-services]">
            
          </div> */}
          <div className="[wrapper] max-w-md w-full">
            <TwAppBar
              isBackBtnShow={true}
              onBackClick={() => { navigate(-1) }}
              title={t("payment,R&P")}
            />
            <div className="[content] flex flex-col gap-6 bg-white  min-w-full">
              <EventInfoCard
                reduxstate={reduxstate}
                selectedStep={5}
                isReadMore={false}
                isReview={true}
                toggleReadMore={() => { }}
              />
              <UserDetails />
              <EventBuyingSummary reduxstate={reduxstate} />

              <div className=" pb-4 flex flex-col gap-4">
                <div className="px-4 bg-white">
                  <div className="flex justify-between items-center gap-4 border-2 border-gray-300 rounded-xl p-4">
                    <div className="flex gap-2 items-center">
                      <CardIcon w={rem(32)} h={rem(32)} />
                      <div className="flex flex-col">
                        <div className="text-lg font-bold">
                          Card Pay
                        </div>
                        <div className="text-sm text-gray-500">
                        </div>
                      </div>
                    </div>
                    <TwButton
                      variant={"secondary-link"}
                      className="!text-lg"
                      onClick={handleChangePaymentMethod}
                    >
                      {t("common.change")}
                    </TwButton>
                  </div>
                </div>
              </div>

            </div>
            <CardPayment clientSecret={clientSecret} />
          </div>
        </Elements>
      ) : (
        <div className="ticket-view-loading">
          <Loader size={30} thin={2} />
        </div>
      )}
    </>
  );
}
